import { useEffect, type ReactNode } from 'react';
import { useLogin } from './useLogin';
import { useAuthStore } from './store';

interface AuthGateProps {
  children: ReactNode;
}

/**
 * Wraps the whole app. On mount: triggers login with Telegram initData.
 * Shows loading/error states until auth resolves.
 */
export function AuthGate({ children }: AuthGateProps) {
  const { login, status } = useLogin();
  const runtime = useAuthStore((s) => s.runtime);
  const isReady = useAuthStore((s) => s.isReady);

  useEffect(() => {
    if (!isReady) {
      void login();
    }
  }, [isReady, login]);

  if (status.state === 'loading' || (status.state === 'idle' && !isReady)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Авторизация...</p>
        </div>
      </div>
    );
  }

  if (status.state === 'error') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white p-6">
        <div className="text-center max-w-xs">
          <p className="text-2xl mb-3">😕</p>
          <h1 className="text-lg font-semibold text-gray-900 mb-2">Не удалось авторизоваться</h1>
          <p className="text-sm text-gray-500 mb-6">
            {runtime === 'telegram'
              ? 'Произошла ошибка при входе через Telegram. Попробуйте ещё раз.'
              : 'Произошла ошибка при восстановлении сессии. Попробуйте ещё раз.'}
          </p>
          <button
            onClick={() => void login()}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium"
          >
            Повторить
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
